import { useState } from 'react';
import { Modal } from 'antd';
import CodeInput, { emptyCode } from '../../../reusable/CodeInput/CodeInput';
import { authErrorMessage } from '../../../store/api/authHelpers';
import { useVerify2faMutation } from '../../../store/api/authApi';
import { t } from '../../../i18n/i18n';
import './TwoFactorDialog.scss';

const CODE_LENGTH = 6;

/**
 * The sign-in challenge. `/email/signin/` answers `requires_2fa` with a
 * `temp_token` instead of credentials; this spends that token together with the
 * code from the authenticator app, and `verify2fa` puts the real session in the
 * store on the way through.
 *
 * A dialog rather than a second route: the password is already accepted, the
 * form behind it is exactly what the user returns to on cancel, and there is no
 * URL worth being able to reload — the token dies with the attempt.
 */
export default function TwoFactorDialog({ open, tempToken, remember, onCancel, onVerified }) {
  const [verify2fa, { isLoading }] = useVerify2faMutation();
  const [digits, setDigits] = useState(emptyCode(CODE_LENGTH));
  const [error, setError] = useState(null);

  const code = digits.join('');

  const close = () => {
    setDigits(emptyCode(CODE_LENGTH));
    setError(null);
    onCancel();
  };

  const submit = async (otpCode = code) => {
    if (isLoading) return;
    if (otpCode.length < CODE_LENGTH) {
      setError(t('Enter all 6 digits from your authenticator app'));
      return;
    }
    setError(null);
    try {
      // `remember` is the form's checkbox, carried through to the session this
      // call creates — the dialog itself has nothing to ask about it.
      await verify2fa({ tempToken, otpCode, remember }).unwrap();
      setDigits(emptyCode(CODE_LENGTH));
      onVerified();
    } catch (err) {
      setError(t(authErrorMessage(err)));
      setDigits(emptyCode(CODE_LENGTH));
    }
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      centered
      width={392}
      mask={{ closable: !isLoading }}
      destroyOnHidden
      className="tfa-dialog"
      rootClassName="tfa-dialog-root"
      aria-label={t('Two-factor authentication')}
    >
      {/* A form so Enter in any box submits, the way it would on the sign-in form. */}
      <form
        className="tfa-dialog__body"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <span className="tfa-dialog__badge">
          <span className="icon tfa-dialog__glyph" style={{ '--icon': 'url(/icons/profile-2fa.svg)' }} />
        </span>

        <div className="tfa-dialog__head">
          <h2 className="tfa-dialog__title">{t('Two-factor authentication')}</h2>
          <p className="tfa-dialog__desc">
            {t('Enter the 6-digit code your authenticator app is showing for Alpha Capital.')}
          </p>
        </div>

        <CodeInput
          digits={digits}
          onChange={(next) => {
            setDigits(next);
            setError(null);
            // The sixth digit (typed or pasted) is the whole answer — send it
            // straight away rather than spend the code's 30 seconds on a click.
            const nextCode = next.join('');
            if (nextCode.length === CODE_LENGTH) submit(nextCode);
          }}
          invalid={Boolean(error)}
          disabled={isLoading}
          autoFocus={open}
        />

        {error ? (
          <p className="tfa-dialog__error" role="alert">
            {error}
          </p>
        ) : (
          <p className="tfa-dialog__hint">{t('The code changes every 30 seconds.')}</p>
        )}

        <div className="tfa-dialog__actions">
          <button type="button" className="tfa-dialog__cancel" onClick={close} disabled={isLoading}>
            {t('Cancel')}
          </button>
          <button type="submit" className="tfa-dialog__submit" disabled={isLoading}>
            {isLoading ? t('Verifying…') : t('Verify and sign in')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
